import React, { useState, useEffect, useRef } from 'react';
import { Megaphone, Gift, Star } from 'lucide-react';

interface Step {
	id: number;
	title: string;
	description: string;
	icon: React.ReactNode;
	badge: string;
	navLabel: string;
}

const ReferralTimeline: React.FC = () => {
	const [activeStep, setActiveStep] = useState<number>(1);
	const [isScrolled, setIsScrolled] = useState<boolean>(false);
	const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

	// JS sticky refs
	const navRef = useRef<HTMLDivElement | null>(null);
	const placeholderRef = useRef<HTMLDivElement | null>(null);
	const navOriginalTopRef = useRef<number | null>(null);

	const steps: Step[] = [
		{
			id: 1,
			title: 'Refer Your Friends',
			description: "Share your unique referral link with friends, family, and colleagues who could benefit from our services. It's quick and easy to spread the word.",
			icon: <Megaphone className="w-6 h-6 md:w-8 md:h-8" />,
			badge: 'Refer Now',
			navLabel: 'Refer Now'
		},
		{
			id: 2,
			title: 'Friend Gets $100(AUD) Service Credit',
			description: 'When your friend signs up using your referral link, they automatically receive a $100(AUD) service credit to use on their first project with us.',
			icon: <Gift className="w-6 h-6 md:w-8 md:h-8" />,
			badge: 'Friend Benefit',
			navLabel: 'Friend Benefit'
		},
		{
			id: 3,
			title: 'You Earn $150(AUD) Service Credit',
			description: "Once your friend completes their first project, you'll receive a $150(AUD) service credit as our thank you for the referral. Everyone wins!",
			icon: <Star className="w-6 h-6 md:w-8 md:h-8" />,
			badge: 'Your Reward',
			navLabel: 'You Reward'
		}
	];

	useEffect(() => {
		const nav = navRef.current;
		const placeholder = placeholderRef.current;
		if (!nav || !placeholder) return;

		const STICK_OFFSET = 20;

		const recordOriginalTop = () => {
			// Reset to normal flow first so we measure correctly
			placeholder.style.display = 'none';
			nav.style.position = 'relative';
			nav.style.top = '';
			nav.style.left = '';
			nav.style.transform = '';
			nav.style.width = '';
			nav.style.zIndex = '';
			navOriginalTopRef.current = nav.getBoundingClientRect().top + window.scrollY;
		};

		recordOriginalTop();

		const handleScroll = () => {
			const scrollY = window.scrollY;
			setIsScrolled(scrollY > 50);

			if (navOriginalTopRef.current !== null) {
				const shouldStick = scrollY + STICK_OFFSET >= navOriginalTopRef.current;

				if (shouldStick) {
					nav.style.position = 'fixed';
					nav.style.top = `${STICK_OFFSET}px`;
					nav.style.left = '50%';
					nav.style.transform = 'translateX(-50%)';
					nav.style.zIndex = '40'; // ✅ Lowered from 9999 so navbar sits above
					placeholder.style.display = 'block';
					placeholder.style.height = nav.offsetHeight + 16 + 'px';
				} else {
					nav.style.position = 'relative';
					nav.style.top = '';
					nav.style.left = '';
					nav.style.transform = '';
					nav.style.zIndex = '';
					placeholder.style.display = 'none';
				}
			}

			// Active step tracking
			let currentStep: number | null = null;
			let maxVisibility = 0;

			stepRefs.current.forEach((stepElement, index) => {
				if (stepElement) {
					const rect = stepElement.getBoundingClientRect();
					const windowHeight = window.innerHeight;
					const visibleTop = Math.max(0, rect.top);
					const visibleBottom = Math.min(windowHeight, rect.bottom);
					const visibleHeight = Math.max(0, visibleBottom - visibleTop);
					const visibility = visibleHeight / rect.height;

					if (visibility > maxVisibility && visibility > 0.3) {
						maxVisibility = visibility;
						currentStep = index + 1;
					}
				}
			});

			if (currentStep) setActiveStep(currentStep);
		};

		window.addEventListener('scroll', handleScroll, { passive: true });
		window.addEventListener('resize', recordOriginalTop);
		handleScroll();

		return () => {
			window.removeEventListener('scroll', handleScroll);
			window.removeEventListener('resize', recordOriginalTop);
		};
	}, []);

	const navigateToStep = (stepNumber: number) => {
		const stepElement = stepRefs.current[stepNumber - 1];
		if (stepElement) {
			const yOffset = -100;
			const y = stepElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
			window.scrollTo({ top: y, behavior: 'smooth' });
		}
	};

	return (
		<section className="relative overflow-visible pt-16 md:pt-24 pb-8 md:pb-12">
			<div className="container mx-auto px-4">

				{/* Section Header */}
				<div className="row justify-center mb-12 md:mb-16">
					<div className="max-w-2xl mx-auto text-center">
						<h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
							Referral Rewards Program
						</h2>
						<p className="text-base md:text-lg text-gray-300">
							Share the love and earn rewards. It's that simple.
						</p>
					</div>
				</div>

				{/* Placeholder - reserves space when nav is fixed */}
				<div ref={placeholderRef} style={{ display: 'none' }} />

				{/* JS Sticky Nav - HIDDEN ON MOBILE */}
				<div
					ref={navRef}
					className={`hidden md:flex items-center justify-center transition-colors duration-300 mb-4 rounded-full px-6 py-4 mx-auto w-fit ${
						isScrolled
							? 'bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-md shadow-xl border border-purple-500/50'
							: 'bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-sm shadow-lg border border-purple-500/20'
					}`}
				>
					<div className="flex items-center justify-center gap-3">
						{steps.map((step, index) => (
							<React.Fragment key={step.id}>
								<button
									onClick={() => navigateToStep(step.id)}
									className={`flex flex-row items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 flex-shrink-0 ${
										// ✅ Changed from rounded-2xl to rounded-full
										activeStep === step.id
											? 'bg-gradient-to-br from-purple-600 to-blue-600 scale-105'
											: 'bg-transparent hover:bg-white/5 hover:-translate-y-1'
									}`}
								>
									<span
										className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
											activeStep === step.id
												? 'bg-white/20 text-white border-2 border-white/30 scale-110'
												: 'bg-gradient-to-br from-purple-400/20 to-blue-500/20 text-purple-300 border-2 border-purple-400/30 hover:border-purple-300 hover:scale-110'
										}`}
									>
										{step.id.toString().padStart(2, '0')}
									</span>
									<span
										className={`text-xs font-semibold uppercase tracking-wide transition-colors duration-300 whitespace-nowrap ${
											activeStep === step.id ? 'text-white' : 'text-purple-200/90'
										}`}
									>
										{step.navLabel}
									</span>
								</button>

								{index < steps.length - 1 && (
									<div className="w-6 h-0.5 bg-gradient-to-r from-purple-400/30 via-purple-500/50 to-purple-400/30 flex-shrink-0" />
								)}
							</React.Fragment>
						))}
					</div>
				</div>

				{/* Timeline */}
				<div className="max-w-4xl mx-auto pt-8 md:pt-12">
					{steps.map((step, index) => (
						<div
							key={step.id}
							ref={(el) => { stepRefs.current[index] = el; }}
							className="relative flex gap-0 md:gap-6 mb-8 md:mb-12"
						>
							{/* Left Side - Number Circle with Connector Line */}
							<div className="relative flex-shrink-0 w-16 md:w-20">
								<div className="relative w-14 h-14 md:w-20 md:h-20 flex items-center justify-center z-10 group">
									<span className="text-lg md:text-3xl font-bold text-white relative z-30">
										{step.id.toString().padStart(2, '0')}
									</span>
									<div className="absolute w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 rounded-full shadow-xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-12 group-hover:shadow-2xl group-hover:shadow-purple-500/60" />
								</div>

								{index < steps.length - 1 && (
									<div className="absolute left-7 md:left-10 top-14 md:top-20 w-0.5 h-[calc(100%+2rem)] md:h-[calc(100%-3rem)] bg-gradient-to-b from-purple-400/50 via-purple-500/70 to-blue-500/60" />
								)}
							</div>

							{/* Right Side - Content Card */}
							<div className="flex-1 pb-4 md:pb-0">
								<div className="relative bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-xl p-5 md:p-8 rounded-2xl shadow-xl border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 hover:md:translate-x-3 hover:-translate-y-1 hover:shadow-2xl group">
									<div className="absolute top-0 left-0 w-0 h-1 bg-gradient-to-r from-purple-500 to-blue-500 rounded-t-2xl transition-all duration-300 origin-left group-hover:w-full" />

									<div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-purple-600/20 to-blue-600/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-4 md:mb-5 shadow-lg border border-purple-500/30 transition-all duration-300 group-hover:from-purple-600 group-hover:to-blue-600 group-hover:scale-110 group-hover:border-purple-400">
										<span className="text-purple-300 transition-all duration-300 group-hover:text-white group-hover:rotate-12">
											{step.icon}
										</span>
									</div>

									<h4 className="text-lg md:text-2xl font-bold text-white mb-2 md:mb-3 transition-colors duration-300 group-hover:text-purple-300">
										{step.title}
									</h4>

									<p className="text-sm md:text-base leading-relaxed text-gray-300 mb-4 md:mb-5">
										{step.description}
									</p>

									{/* <div className="inline-block px-4 md:px-5 py-1.5 md:py-2 bg-purple-500/15 text-purple-300 rounded-full text-xs md:text-sm font-semibold uppercase tracking-wide border border-purple-500/30">
										{step.badge}
									</div> */}
								</div>
							</div>
						</div>
					))}
				</div>

			</div>
		</section>
	);
};

export default ReferralTimeline;